// Live word-by-word transcript for the listening caption, mirroring the kiosk's
// LiveTranscript component: the sentence constructs itself while the visitor
// speaks. Interim STT revisions (Google constantly rewrites the tail: casing,
// punctuation, corrections) swap a word's text in place without re-animating it,
// so nothing flashes; genuinely new words fade in letter by letter (anime.js —
// the "ml3" text reveal, sped up for live captions). The browser's centered
// inline layout re-wraps and re-centers the sentence as it grows.
import type { animate as animateFn, stagger as staggerFn } from 'animejs';

// Faster than the classic anime.js ml3 demo (2250ms / 150ms-per-letter): words
// must keep up with speech, so each letter fades in ~200ms with a short stagger.
const LETTER_FADE_MS = 200;
const LETTER_STAGGER_MS = 24;
// Words arriving in the same interim batch cascade slightly, like the kiosk's
// WORD_STAGGER (0.12s), but tightened for the small mobile caption.
const WORD_STAGGER_MS = 80;

type AnimeApi = { animate: typeof animateFn; stagger: typeof staggerFn };
let anime: AnimeApi | null = null;
let animeLoad: Promise<void> | null = null;

/** Load anime.js once, best-effort: if it fails, words simply appear unanimated. */
export function preloadLiveTranscript(): void {
  if (!animeLoad) {
    animeLoad = import('animejs')
      .then(({ animate, stagger }) => {
        anime = { animate, stagger };
      })
      .catch(() => {
        /* words appear without the fade; the transcript still works */
      });
  }
}

interface LiveWord {
  text: string;
  el: HTMLSpanElement;
}

let host: HTMLElement | null = null;
let sentenceEl: HTMLDivElement | null = null;
let words: LiveWord[] = [];

function buildWordEl(text: string, animated: boolean, batchIndex: number): HTMLSpanElement {
  const word = document.createElement('span');
  word.className = 'live-word';
  const letters: HTMLSpanElement[] = [];
  for (const ch of text) {
    const letter = document.createElement('span');
    letter.className = 'live-letter';
    letter.textContent = ch;
    if (animated && anime) letter.style.opacity = '0';
    letters.push(letter);
    word.appendChild(letter);
  }
  if (animated && anime && letters.length) {
    anime.animate(letters, {
      opacity: [0, 1],
      ease: 'inOutQuad',
      duration: LETTER_FADE_MS,
      delay: anime.stagger(LETTER_STAGGER_MS, { start: batchIndex * WORD_STAGGER_MS }),
    });
  }
  return word;
}

/**
 * Reflect the latest transcript snapshot into `container`. Diff-based like the
 * kiosk: revised words swap in place (fully visible, no re-animation), trailing
 * words dropped by a shrunken interim are removed, appended words animate in.
 */
export function updateLiveTranscript(container: HTMLElement, text: string): void {
  preloadLiveTranscript();
  if (host !== container || !sentenceEl) {
    clearLiveTranscript();
    host = container;
    // The caption is a flex column; one wrapper block keeps the words in normal
    // centered inline flow (individual spans would stack as flex items).
    sentenceEl = document.createElement('div');
    sentenceEl.className = 'live-transcript';
    container.textContent = '';
    container.appendChild(sentenceEl);
  }

  const newTexts = text.split(/\s+/).filter(Boolean);

  // In-place revision of already-shown words: keep them fully visible.
  for (let i = 0; i < Math.min(words.length, newTexts.length); i++) {
    if (words[i].text !== newTexts[i]) {
      const el = buildWordEl(newTexts[i], false, 0);
      words[i].el.replaceWith(el);
      words[i] = { text: newTexts[i], el };
    }
  }

  // Shrunken interim: drop trailing words.
  while (words.length > newTexts.length) words.pop()!.el.remove();

  // Append genuinely new words, staggered within this batch.
  let batchIndex = 0;
  for (let i = words.length; i < newTexts.length; i++) {
    const el = buildWordEl(newTexts[i], true, batchIndex++);
    sentenceEl.appendChild(el);
    words.push({ text: newTexts[i], el });
  }
}

/** Reset for the next recording session (leaves the container ready for plain text). */
export function clearLiveTranscript(): void {
  words = [];
  sentenceEl?.remove();
  sentenceEl = null;
  host = null;
}
